import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const dateCivile = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ');
const codePiece = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code de type de pièce invalide');

/** Statuts visibles par l'usager (H3-03) et leurs libellés. */
export const STATUTS_USAGER = {
  brouillon: 'Brouillon',
  enregistre: 'Enregistré',
  en_verification: 'En vérification',
  incomplet: 'Incomplet',
  en_instruction: 'En instruction',
  en_attente_decision: 'En attente de décision',
  approuve: 'Approuvé',
  refuse: 'Refusé',
  termine: 'Terminé',
  classe_sans_suite: 'Classé sans suite',
  retire: 'Retiré',
} as const;
export type StatutUsager = keyof typeof STATUTS_USAGER;

export const RESULTATS_DECISION = ['approuve', 'approuve_sous_conditions', 'refuse'] as const;
export type ResultatDecision = (typeof RESULTATS_DECISION)[number];

/** États après lesquels un dossier n'est plus « en cours » (indicateurs DOS-02, DOS-04, DOS-05). */
export const ETATS_DOSSIER_FINAUX = ['cloture', 'archive', 'classe_sans_suite', 'retire'] as const;
export const ETATS_DOSSIER_NON_RECUS = ['brouillon', 'depose'] as const;

export const VERDICTS_PIECE = ['non_examinee', 'conforme', 'non_conforme'] as const;

export const schemaPieceJointe = z.object({ document_id: uuid, type: codePiece });

/** Précision d'une localisation (mission géolocalisation, priorité 4). */
export const PRECISIONS_LOCALISATION = { adresse: 'Adresse', parcelle: 'Parcelle', gps: 'Relevé GPS', approximatif: 'Approximatif' } as const;
export type PrecisionLocalisation = keyof typeof PRECISIONS_LOCALISATION;

/** Position WGS84 (EPSG:4326) d'un dossier et précision de la saisie. */
export const schemaLocalisation = z.object({
  longitude: z.number().min(-180).max(180),
  latitude: z.number().min(-90).max(90),
  precision: z.enum(Object.keys(PRECISIONS_LOCALISATION) as [PrecisionLocalisation, ...PrecisionLocalisation[]]).default('approximatif'),
});
export type Localisation = z.infer<typeof schemaLocalisation>;

/** Emprise « ouest,sud,est,nord » en degrés, pour GET /dossiers?bbox=. */
export const schemaBbox = z
  .string()
  .regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?$/, 'Emprise au format ouest,sud,est,nord')
  .transform((s) => s.split(',').map(Number) as [number, number, number, number])
  .refine((b) => b[0] >= -180 && b[2] <= 180 && b[1] >= -90 && b[3] <= 90 && b[0] < b[2] && b[1] < b[3], { message: 'Emprise invalide' });

export const schemaCreationDossier = z
  .object({
    localisation: schemaLocalisation.optional(),
    demarche_version_id: uuid,
    usager_compte_id: uuid.optional(),
    tiers_nom: z.string().trim().min(1).max(300).optional(),
    tiers_contact: z
      .object({ telephone: z.string().trim().max(40).optional(), courriel: z.string().trim().email().optional(), adresse: z.string().trim().max(500).optional() })
      .optional(),
    service_id: uuid.optional(),
    arrondissement_id: uuid.optional(),
    instructeur_appartenance_id: uuid.optional(),
    courrier_origine_id: uuid.optional(),
    // Valeurs composées (adresse, localisation, choix multiple, tableau) : formulaires de l'éditeur
    // de démarches (L1-A), validés ensuite par le moteur de formulaires de la version.
    donnees_formulaire: z.record(z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), z.union([z.string().max(5000), z.number(), z.boolean(), z.null(), z.array(z.unknown()).max(500), z.record(z.string(), z.unknown())])).default({}),
    pieces: z.array(schemaPieceJointe).max(50).default([]),
  })
  .refine((v) => v.usager_compte_id || v.tiers_nom, { message: 'Indiquer le demandeur (compte usager ou nom)', path: ['tiers_nom'] });

export const schemaTransitionDossier = z.object({
  motif: z.string().trim().max(2000).optional(),
  pieces: z.array(schemaPieceJointe).max(50).default([]),
  delegation_id: uuid.optional(),
  /** Décision (transition decider) : résultat, motif obligatoire en cas de refus, voies de recours. */
  resultat: z.enum(RESULTATS_DECISION).optional(),
  voies_recours: z.string().trim().max(2000).optional(),
  /** Transmission : service destinataire. */
  service_id: uuid.optional(),
});

export const schemaAjoutPiece = z.object({
  document_id: uuid,
  type_piece: codePiece,
});

export const schemaVerdictPiece = z
  .object({
    verdict: z.enum(['conforme', 'non_conforme']),
    motif: z.string().trim().min(3).max(1000).optional(),
  })
  .refine((v) => v.verdict !== 'non_conforme' || v.motif, { message: 'Motif obligatoire pour une pièce non conforme', path: ['motif'] });

export const schemaNoteInterne = z.object({
  contenu: z.string().trim().min(1).max(10000),
});

export const schemaFiltreDossiers = z.object({
  etat: z.string().regex(/^[a-z_]+$/).optional(),
  statut_usager: z.enum(Object.keys(STATUTS_USAGER) as [StatutUsager, ...StatutUsager[]]).optional(),
  service: uuid.optional(),
  arrondissement: uuid.optional(),
  echeance_avant: dateCivile.optional(),
  en_retard: z.enum(['oui', 'non']).optional(),
  hors_delai: z.enum(['oui', 'non']).optional(),
  q: z.string().trim().min(1).max(200).optional(),
  /** Dossiers dont la localisation est dans l'emprise (carte du territoire). */
  bbox: schemaBbox.optional(),
  localise: z.enum(['oui', 'non']).optional(),
  limite: z.coerce.number().int().min(1).max(500).default(50),
  apres: z.string().max(200).optional(),
});

// Réponses ---------------------------------------------------------------------------------

export const schemaDossier = z.object({
  id: uuid,
  numero: z.string().nullable(),
  demarche: z.object({ id: uuid, code: z.string(), libelle: z.string(), version_id: uuid, numero_version: z.number() }),
  demandeur: z.object({ usager_compte_id: z.string().nullable(), nom: z.string().nullable() }),
  canal_depot: z.string(),
  service: z.object({ id: uuid, nom: z.string() }).nullable(),
  arrondissement: z.object({ id: uuid, nom: z.string() }).nullable(),
  instructeur_appartenance_id: z.string().nullable(),
  etat: z.string(),
  statut_usager: z.string(),
  statut_usager_libelle: z.string(),
  resultat: z.enum(RESULTATS_DECISION).nullable(),
  motif_decision: z.string().nullable(),
  decide_le: z.string().nullable(),
  donnees_formulaire: z.record(z.string(), z.unknown()),
  enregistre_le: z.string().nullable(),
  echeance_reglementaire: z.string().nullable(),
  echeance_interne: z.string().nullable(),
  suspendu: z.boolean(),
  nb_reouvertures: z.number(),
  en_retard: z.boolean(),
  hors_delai_reglementaire: z.boolean(),
  workflow_instance_id: z.string().nullable(),
  localisation: schemaLocalisation.nullable(),
});
export type Dossier = z.infer<typeof schemaDossier>;

export const schemaPieceDossier = z.object({
  id: uuid,
  type_piece: z.string(),
  document_id: uuid,
  titre: z.string(),
  verdict: z.enum(VERDICTS_PIECE),
  motif: z.string().nullable(),
  examinee_le: z.string().nullable(),
});

export const schemaFicheDossier = schemaDossier.extend({
  pieces: z.array(schemaPieceDossier),
  pieces_attendues: z.array(z.object({ type: z.string(), libelle: z.string(), obligatoire: z.boolean() })),
  transitions_possibles: z.array(z.object({ code: z.string(), libelle: z.string(), vers: z.string(), motif_obligatoire: z.boolean() })),
});

export const schemaListeDossiers = z.object({ elements: z.array(schemaDossier), suivant: z.string().nullable() });

export const schemaNote = z.object({
  id: uuid,
  contenu: z.string(),
  auteur: z.object({ appartenance_id: uuid, nom: z.string().nullable() }),
  cree_le: z.string(),
});
export const schemaListeNotes = z.object({ elements: z.array(schemaNote) });
